/* eslint-disable no-shadow,no-use-before-define */
import { useI18nForVue2 } from '@invivodf/locales/src/useI18n'
import {
  buildIndexName,
  clearAccessKey,
  configureAccessKey,
  createProductIndex,
  createSearchClient,
  fetchAccessKey,
  registerSecuredClient,
  resolveProductIndexName,
} from '@invivodf/front-seller-search'
import { checkFeature, FeatureType } from '@invivodf/front-seller-feature-flipping'
import Vue from 'vue'
import Vuex from 'vuex'
import { useAuthService } from '@b2ag/auth'

import { securedKeyProductSdk } from '@/core/dependency-injection'
import { AladinRoleType, getAladinRoles } from '@/auth'
import { isB2agContext } from '@invivodf/common/src/application/composables/useContext'
import { BRAND_MANAGE_SCOPES } from '@invivodf/front-seller-auth/src/BRAND_MANAGE_SCOPES'
import { PRODUCT_MANAGER_MANAGE_SCOPES } from '@invivodf/front-seller-auth/src/PRODUCT_MANAGER_MANAGE_SCOPES'
import { cooperativeService, offerService, userService } from '@/services'
import catalog from './catalog.store'
import { getOfferStoreModule } from './offers.store'
import quota from './quota.store'
import stockQuantity from './stockQuantity.store'
import toasts from './toasts.store'

Vue.use(Vuex)

// Izanami flag (Dynamic, brand-prefixed) driving the v2 -> v3 mono-index switch.
const V3_MONO_INDEX_FLAG = 'indexation:v3-mono-index'

// The b2ag Vuex store is imported (but unused) in the Retail context, where no
// Algolia credentials are provided. Only build the client in the B2ag world so
// importing this module does not throw in Retail.
const searchClient = isB2agContext() ? createSearchClient(window.env.ALGOLIA_APP_ID, window.env.ALGOLIA_API_KEY) : null

if (searchClient) {
  // The search-only client is a session singleton: we register it so its key is
  // rotated in place onto the catalog_manager Secured API Key (and back to the public
  // key on logout). The provider forwards the JWT through the product SDK -> the BFF
  // derives the catalog_manager profile from the seller role (QUOKKA-2330).
  registerSecuredClient(searchClient)
  configureAccessKey({
    securedKeyProvider: ({ distributorId, locale }) =>
      securedKeyProductSdk.getDistributorSecuredKey({ distributorId, locale }),
    publicApiKey: window.env.ALGOLIA_API_KEY,
    onError: (error) => console.error('Failed to fetch the Algolia Secured API Key', error),
  })
}

// Builds the cooperative product index: the v3 distributor index carried by the Secured
// API Key when cached (v3 flag active), otherwise the default v2 index.
function buildCooperativeIndex(cooperativeId, locale) {
  if (!searchClient || !cooperativeId) return null
  const v2IndexName = buildIndexName(window.env.ALGOLIA_INDEX_COOP_PREFIX, cooperativeId, locale)
  return createProductIndex(searchClient, resolveProductIndexName(v2IndexName))
}

const defaultUser = { infos: {} }

const globalState = {
  App: {
    isAuthenticated: false,
  },
  user: defaultUser,
  cooperative: {},
  membershipImportInformation: {},
  shippingAddressesImportInformation: {},
  storesImportInformation: {},
  technicalSalesImportInformation: {},
  customProductImportInformation: {},
  partnerContactImportInformation: {},
  customerStoreImportInformation: {},
  cooperativeIndex: null,
  isV3SearchEnabled: false,
  isTech: false,
  hasBrandManagePermission: false,
  hasProductManagerManagePermission: false,
  isPartnerWithNewOfferConfiguration: false,
  usersRolesImportInformation: {},
  salesStoresImportInformation: {},
}

export const getters = {
  user(state) {
    return state.user
  },
  isAuthenticated(state) {
    return state.App.isAuthenticated
  },
  cooperative(state) {
    return state.cooperative
  },
  cooperativeId(state) {
    return state.cooperative.id
  },
  cooperativeName(state) {
    return state.cooperative.name
  },
  cooperativeIndex(state) {
    return state.cooperativeIndex
  },
  isV3SearchEnabled(state) {
    return state.isV3SearchEnabled
  },
  membershipImportInformation(state) {
    return state.membershipImportInformation
  },
  shippingAddressesImportInformation(state) {
    return state.shippingAddressesImportInformation
  },
  storesImportInformation(state) {
    return state.storesImportInformation
  },
  technicalSalesImportInformation(state) {
    return state.technicalSalesImportInformation
  },
  partnerContactImportInformation(state) {
    return state.partnerContactImportInformation
  },
  customerStoreImportInformation(state) {
    return state.customerStoreImportInformation
  },
  customProductImportInformation(state) {
    return state.customProductImportInformation
  },
  isTech(state) {
    return state.isTech
  },
  hasBrandManagePermission(state) {
    return state.hasBrandManagePermission
  },
  hasProductManagerManagePermission(state) {
    return state.hasProductManagerManagePermission
  },
  isUserLoaded(state) {
    return state.user !== defaultUser
  },
  isPartnerWithNewOfferConfiguration(state) {
    return state.isPartnerWithNewOfferConfiguration
  },
  usersRolesImportInformation(state) {
    return state.usersRolesImportInformation
  },
  salesStoresImportInformation(state) {
    return state.salesStoresImportInformation
  },
}

export const mutations = {
  SET_USER(state, user) {
    if (user) {
      state.user = { ...user, current_order: user.current_order || { line_items: [] } }
    }
  },
  RESET_USER(state) {
    state.user = defaultUser
    state.App.isAuthenticated = false
  },
  SET_AUTHENTICATED(state, isAuthenticated) {
    state.App.isAuthenticated = isAuthenticated
  },
  SET_COOPERATIVE(state, cooperative) {
    const { locale } = useI18nForVue2()
    state.cooperative = cooperative
    state.cooperativeIndex = buildCooperativeIndex(cooperative.id, locale)
  },
  // Rebuilds the cooperative index after a Secured API Key has been obtained
  // (v2 -> v3 distributor index carried by the key).
  REFRESH_COOPERATIVE_INDEX(state) {
    const { locale } = useI18nForVue2()
    state.cooperativeIndex = buildCooperativeIndex(state.cooperative.id, locale)
  },
  SET_V3_SEARCH_ENABLED(state, isEnabled) {
    state.isV3SearchEnabled = isEnabled
  },
  SET_MEMBERSHIP_IMPORT_INFORMATION(state, cooperative) {
    state.membershipImportInformation = {
      lastMembershipsImportAt: cooperative.lastMembershipsImportAt,
      membershipCount: cooperative.membershipCount,
    }
  },
  SET_SHIPPING_ADDRESSES_IMPORT_INFORMATION(state, cooperative) {
    state.shippingAddressesImportInformation = {
      lastShippingAddressesImportAt: cooperative.lastShippingAddressesImportAt,
      shippingAddressesCount: cooperative.shippingAddressesCount,
    }
  },
  SET_STORES_IMPORT_INFORMATION(state, cooperative) {
    state.storesImportInformation = {
      lastStoresImportAt: cooperative.lastStoresImportAt,
      storesCount: cooperative.storesCount,
    }
  },
  SET_TECHNICAL_SALES_IMPORT_INFORMATION(state, cooperative) {
    state.technicalSalesImportInformation = {
      lastTechnicalSalesImportAt: cooperative.lastTechnicalSalesImportAt,
      technicalSalesCount: cooperative.technicalSalesCount,
    }
  },
  SET_PARTNER_CONTACT_IMPORT_INFORMATION(state, cooperative) {
    state.partnerContactImportInformation = {
      lastCustomerContactsImportAt: cooperative.lastCustomerContactsImportAt,
      customerContactsCount: cooperative.customerContactsCount,
    }
  },
  SET_CUSTOMER_STORE(state, cooperative) {
    state.customerStoreImportInformation = {
      lastCustomerStoreImportAt: cooperative.lastCustomerStoreImportAt,
      customerStoreCount: cooperative.customerStoreCount,
    }
  },
  SET_CUSTOM_PRODUCT_IMPORT_INFORMATION(state, importReport) {
    state.customProductImportInformation = {
      lastCustomProductImportAt: importReport.lastCustomProductImportAt,
      customProductCount: importReport.customProductCount,
    }
  },
  SET_IS_TECH(state, isTech) {
    state.isTech = isTech
  },
  SET_HAS_BRAND_MANAGE_PERMISSION(state, value) {
    state.hasBrandManagePermission = value
  },
  SET_HAS_PRODUCT_MANAGER_MANAGE_PERMISSION(state, value) {
    state.hasProductManagerManagePermission = value
  },
  SET_PARTNER_WITH_NEW_OFFER_CONFIGURATION(state, value) {
    state.isPartnerWithNewOfferConfiguration = value
  },
  SET_USERS_ROLES_IMPORT_INFORMATION(state, partner) {
    state.usersRolesImportInformation = {
      lastUserRolesImportAt: partner.lastUserRolesImportAt,
      userRolesCount: partner.userRolesCount,
    }
  },
  SET_SALES_STORES_IMPORT_INFORMATION(state, partner) {
    state.salesStoresImportInformation = {
      lastSalesStoresImportAt: partner.lastSalesStoresImportAt,
      salesStoresCount: partner.salesStoresCount,
    }
  },
}

export const actions = {
  initializeStore(context, cooperative) {
    if (cooperative) {
      context.commit('SET_COOPERATIVE', cooperative)
      context.dispatch('setMembershipImportInformation')
      context.dispatch('setShippingAddressesImportInformation')
      context.dispatch('setStoresImportInformation')
      context.dispatch('setTechnicalSalesImportInformation')
      context.dispatch('setPartnerContactImportInformation')
      context.dispatch('setCustomerStoreImportInformation')
      context.dispatch('setUsersRolesImportInformation')
      context.dispatch('setSalesStoresImportInformation')
    }
  },
  async fetchUser(context) {
    /**
     * @type {import('@/auth').AuthService}
     */
    const authService = useAuthService()
    try {
      const [user, roles, scopes] = await Promise.all([
        userService.get(),
        getAladinRoles(authService),
        authService.getScopes(),
      ])
      const sellerRole = roles[AladinRoleType.seller]
      const sellerPartner = findSellerPartner(user, sellerRole)

      context.commit('SET_USER', user)
      context.commit('SET_AUTHENTICATED', true)
      context.dispatch('initializeStore', sellerPartner)
      context.commit('SET_IS_TECH', Boolean(roles[AladinRoleType.tech]))
      // Same source as the route guard: the roles claim only carries the static seller permissions.
      context.commit(
        'SET_HAS_BRAND_MANAGE_PERMISSION',
        BRAND_MANAGE_SCOPES.some((scope) => (scopes ?? []).includes(scope)),
      )
      context.commit(
        'SET_HAS_PRODUCT_MANAGER_MANAGE_PERMISSION',
        PRODUCT_MANAGER_MANAGE_SCOPES.some((scope) => (scopes ?? []).includes(scope)),
      )
      await context.dispatch('enableV3SearchIfFlagActive')
    } catch (error) {
      if (error.response.status === 401) {
        let opts
        if (this.$route && this.$route.path !== '/') {
          opts = { returnTo: this.$router.resolve({ name: 'home' }).href }
        }
        authService.logout(opts)
      }
    }
  },
  setUser(context, user) {
    context.commit('SET_USER', user)
  },
  // Fetches the catalog_manager Secured API Key (v3 distributor index) when the v3 flag
  // is active for the cooperative, then rebuilds the index on that key. Without the flag
  // we stay on the v2 index with the public key (no regression).
  async enableV3SearchIfFlagActive(context) {
    if (!searchClient) return
    const { cooperativeId } = context.getters
    if (!cooperativeId) return

    let isV3Active = false
    try {
      isV3Active = await checkFeature.execute({
        id: V3_MONO_INDEX_FLAG,
        type: FeatureType.Dynamic,
        context: { id: String(cooperativeId) },
      })
    } catch (error) {
      console.error('Could not check the v3-mono-index flag, keeping v2 search', error)
    }
    if (!isV3Active) {
      context.commit('SET_V3_SEARCH_ENABLED', false)
      return
    }

    const { locale } = useI18nForVue2()
    // catalog_manager: no beneficiaryId, the BFF derives the profile from the seller role (QUOKKA-2330).
    const accessKey = await fetchAccessKey({ distributorId: String(cooperativeId), locale })
    // Flip to v3 only once the Secured API Key is actually held (fetchAccessKey returns null on
    // failure): with the flag on but no key, the v3 query parameters (_catalog_scopes filter,
    // facetingAfterDistinct, variant_name) would be applied to the v2 index and break the search.
    context.commit('SET_V3_SEARCH_ENABLED', Boolean(accessKey))
    if (!accessKey) return
    context.commit('REFRESH_COOPERATIVE_INDEX')
  },
  async logout(context) {
    const authService = useAuthService()
    localStorage.removeItem('cooperative')
    clearAccessKey()
    context.commit('SET_V3_SEARCH_ENABLED', false)
    context.commit('RESET_USER')
    context.commit('SET_COOPERATIVE', {})
    let opts
    if (this.$route && this.$route.path !== '/') {
      opts = { returnTo: this.$router.resolve({ name: 'home' }).href }
    }
    authService.logout(opts)
  },
  async setMembershipImportInformation(context) {
    const cooperative = await cooperativeService.get(context.getters.cooperativeId)
    context.commit('SET_MEMBERSHIP_IMPORT_INFORMATION', cooperative)
  },
  async setShippingAddressesImportInformation(context) {
    const cooperative = await cooperativeService.get(context.getters.cooperativeId)
    context.commit('SET_SHIPPING_ADDRESSES_IMPORT_INFORMATION', cooperative)
  },
  async setStoresImportInformation(context) {
    const cooperative = await cooperativeService.get(context.getters.cooperativeId)
    context.commit('SET_STORES_IMPORT_INFORMATION', cooperative)
  },
  async setTechnicalSalesImportInformation(context) {
    const cooperative = await cooperativeService.get(context.getters.cooperativeId)
    context.commit('SET_TECHNICAL_SALES_IMPORT_INFORMATION', cooperative)
  },
  async setPartnerContactImportInformation(context) {
    const cooperative = await cooperativeService.get(context.getters.cooperativeId)
    context.commit('SET_PARTNER_CONTACT_IMPORT_INFORMATION', cooperative)
  },
  async setCustomerStoreImportInformation(context) {
    const cooperative = await cooperativeService.get(context.getters.cooperativeId)
    context.commit('SET_CUSTOMER_STORE', cooperative)
  },
  async setPartnerWithNewOfferConfiguration(context) {
    const isPartnerWithNewOfferConfiguration = await offerService.isPartnerWithNewOfferConfiguration(
      context.getters.cooperativeId,
    )
    context.commit('SET_PARTNER_WITH_NEW_OFFER_CONFIGURATION', isPartnerWithNewOfferConfiguration)
  },
  async setUsersRolesImportInformation(context) {
    const cooperative = await cooperativeService.get(context.getters.cooperativeId)
    context.commit('SET_USERS_ROLES_IMPORT_INFORMATION', cooperative)
  },
  async setSalesStoresImportInformation(context) {
    const cooperative = await cooperativeService.get(context.getters.cooperativeId)
    context.commit('SET_SALES_STORES_IMPORT_INFORMATION', cooperative)
  },
}

export const modules = {
  toasts,
  offers: getOfferStoreModule(),
  catalog,
  variantOffers: getOfferStoreModule(),
  stockQuantity,
  quota,
}

const store = new Vuex.Store({
  state: globalState,
  getters,
  mutations,
  actions,
  modules,
})

export const getStore = () => store

export default store

function findSellerPartner(user, role) {
  if (!role || !role.profiles[0]) {
    // Should never happen because access to front seller requires the user
    // to have permissions available only to a seller.
    throw new Error('The current user is missing seller role in Aladin access token')
  }
  const sellerProfile = role.profiles[0]
  return user.cooperatives?.filter((partner) => partner.id === sellerProfile.cooperative.id)?.[0]
}
